// hooks/useList.js
import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { debounce } from "lodash";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import errorHandler from "../utils/errorHandler";

export const useList = ({
  fetchData,
  deleteData,
  blockData,
  toggleStatus,
  defaultPageSize = 10,
  debounceDelay = 400,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const pageFromQuery = parseInt(searchParams.get("page") || "1", 10);

  // State management
  const [searchItem, setSearchItem] = useState("");
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(pageFromQuery);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [filterValues, setFilterValues] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [loaderDelay, setLoaderDelay] = useState(false);
  const [refresh, setRefresh] = useState(false);

  // Sync currentPage with query param
  useEffect(() => {
    if (currentPage !== pageFromQuery) {
      setCurrentPage(pageFromQuery);
    }
  }, [pageFromQuery, currentPage]);

  // Update query param when currentPage changes
  useEffect(() => {
    if (currentPage !== pageFromQuery) {
      setSearchParams({ page: currentPage.toString() });
    }
  }, [currentPage, pageFromQuery, setSearchParams]);

  // Fetch data function
  const fetchDataHandler = useCallback(async (query = "") => {
    if (!fetchData) return;
    
    const params = {
      search: query,
      page: currentPage,
      limit: pageSize,
      ...filterValues,
    };

    try {
      setIsLoading(true);
      setLoaderDelay(false);
      
      const response = await fetchData(params);
      
      if (response?.data?.data) {
        const { users = [], data: items = [], pagination = {} } = response.data.data;
        const dataArray = users.length > 0 ? users : items;
        
        setData(dataArray);
        setTotalPages(pagination.total || 0);
      }
      
      setIsLoading(false);
      setLoaderDelay(true);
    } catch (err) {
      setData([]);
      errorHandler(err);
      setIsLoading(false);
      setLoaderDelay(false);
    }
  }, [fetchData, currentPage, pageSize, filterValues]);

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((query) => fetchDataHandler(query), debounceDelay),
    [fetchDataHandler, debounceDelay]
  );

  // Handlers
  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchItem(query);
    debouncedSearch(query);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setSearchParams({ page: page.toString() });
  };

  const handleFilterChange = (filterName, value) => {
    setFilterValues(prev => ({
      ...prev,
      [filterName]: value
    }));
    setCurrentPage(1);
  };

  const handleDelete = async (id, confirmMessage = "Are you sure you want to delete this item?") => {
    if (!deleteData) return;
    
    const result = await Swal.fire({
      text: confirmMessage,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00a1da",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    });

    if (result.isConfirmed) {
      try {
        const res = await deleteData(id);
        if (res?.status === 200) {
          toast.success(res?.data?.message || "Deleted successfully", { toastId: "success" });
          setData(prevData => prevData.filter(item => item._id !== id));
          setRefresh(!refresh);
        } else {
          toast.error(res?.data?.message || "Failed to delete", { toastId: "error" });
        }
      } catch (error) {
        errorHandler(error);
      }
    }
  };

  const handleBlock = async (id, confirmMessage) => {
    if (!blockData) return;
    
    const item = data.find(item => item._id === id);
    const message = confirmMessage ? confirmMessage(item?.isLocked) : 
      `Are you sure you want to ${item?.isLocked ? "unblock" : "block"} this item?`;
    
    const result = await Swal.fire({
      text: message,
      icon: "warning", 
      showCancelButton: true,
      confirmButtonColor: "#00a1da",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    });

    if (result.isConfirmed) {
      try {
        const res = await blockData(id);
        if (res?.status === 200) {
          toast.success(res?.data?.message || "Status updated successfully", { toastId: "success" });
          setRefresh(!refresh);
        } else {
          toast.error(res?.data?.message || "Failed to update status", { toastId: "error" });
        }
      } catch (error) {
        errorHandler(error);
      }
    }
  };

  const handleStatusToggle = async (id, currentStatus) => {
    if (!toggleStatus) return;
    
    try {
      const newStatus = !currentStatus;
      const res = await toggleStatus(id, newStatus);
      
      if (res?.status === 200) {
        toast.success(res?.data?.message || "Status updated successfully");
        setRefresh(!refresh);
      } else {
        toast.error(res?.data?.message || "Failed to update status", { toastId: "error" });
      }
    } catch (err) {
      errorHandler(err);
    }
  };

  const clearSearch = () => {
    setSearchItem("");
    fetchDataHandler("");
  };

  const refreshData = () => {
    setRefresh(!refresh);
  };

  // Effect to fetch data
  useEffect(() => {
    fetchDataHandler(searchItem);
  }, [fetchDataHandler, searchItem, refresh]);

  return {
    // State
    searchItem,
    data,
    totalPages,
    currentPage,
    pageSize,
    filterValues,
    isLoading,
    loaderDelay,
    
    // Handlers
    handleSearchChange,
    handlePageChange,
    handleFilterChange,
    handleDelete,
    handleBlock,
    handleStatusToggle,
    clearSearch,
    refreshData,
    
    // Setters (for advanced usage)
    setSearchItem,
    setPageSize,
    setCurrentPage,
    setFilterValues,
  };
};